import { useState, useRef, useEffect } from 'react'
import { Send, Plus, Phone, MessageSquare } from 'lucide-react'

export default function ChatPage() {
  const [conversations, setConversations] = useState([
    {
      id: '1',
      name: 'John Smith',
      lastMessage: 'Thanks for your help!',
      timestamp: new Date(Date.now() - 5 * 60 * 1000),
      unread: 2,
      channel: 'chat',
      messages: [
        { role: 'customer', text: 'Hi, I need help with my password', timestamp: new Date(Date.now() - 20 * 60 * 1000) },
        { role: 'ai', text: 'I can help with that! Have you tried the forgot password link?', timestamp: new Date(Date.now() - 18 * 60 * 1000) },
        { role: 'customer', text: 'Yes, but I\'m not receiving the email', timestamp: new Date(Date.now() - 15 * 60 * 1000) },
        { role: 'agent', text: 'Let me look into this for you. Can you check your spam folder?', timestamp: new Date(Date.now() - 10 * 60 * 1000) },
        { role: 'customer', text: 'Thanks for your help!', timestamp: new Date(Date.now() - 5 * 60 * 1000) },
      ],
    },
    {
      id: '2',
      name: 'Maria Garcia',
      lastMessage: 'When will the course be available?',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      unread: 0,
      channel: 'sms',
      messages: [
        { role: 'customer', text: 'When will the course be available?', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000) },
      ],
    },
  ])

  const [selectedConv, setSelectedConv] = useState('1')
  const [message, setMessage] = useState('')
  const messagesEndRef = useRef(null)

  const currentConv = conversations.find(c => c.id === selectedConv)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [currentConv?.messages])

  const handleSendMessage = (e) => {
    e.preventDefault()
    if (!message.trim()) return

    setConversations(conversations.map(conv =>
      conv.id === selectedConv
        ? {
            ...conv,
            messages: [
              ...conv.messages,
              { role: 'agent', text: message, timestamp: new Date() },
            ],
            lastMessage: message,
            timestamp: new Date(),
          }
        : conv
    ))
    setMessage('')
  }

  const handleNewChat = () => {
    const newConv = {
      id: String(conversations.length + 1),
      name: 'New Conversation',
      lastMessage: 'No messages yet',
      timestamp: new Date(),
      unread: 0,
      channel: 'chat',
      messages: [],
    }
    setConversations([...conversations, newConv])
    setSelectedConv(newConv.id)
  }

  const handlePhoneCall = () => {
    console.log('Phone call initiated with:', currentConv?.name)
    alert(`Phone call initiated with ${currentConv?.name}`)
  }

  const handleStartChat = () => {
    console.log('Chat with:', currentConv?.name)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Chat</h1>
        <p className="text-blue-200 mt-1">Manage live customer conversations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-96">
        {/* Conversations List */}
        <div className="lg:col-span-1 card overflow-y-auto">
          <div className="p-4 border-b border-blue-500/30">
            <button
              onClick={handleNewChat}
              className="w-full btn-primary flex items-center justify-center space-x-2 cursor-pointer">
              <Plus size={20} />
              <span>New Chat</span>
            </button>
          </div>
          <div className="divide-y divide-blue-500/30">
            {conversations.map(conv => (
              <button
                key={conv.id}
                onClick={() => setSelectedConv(conv.id)}
                className={`w-full p-4 text-left hover:bg-slate-700/30 transition-colors ${
                  selectedConv === conv.id ? 'bg-slate-700/30 border-l-4 border-blue-500/60' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <p className="font-medium text-white text-sm">{conv.name}</p>
                  {conv.unread > 0 && (
                    <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                      {conv.unread}
                    </span>
                  )}
                </div>
                <p className="text-xs text-blue-200 truncate">{conv.lastMessage}</p>
                <p className="text-xs text-blue-300 mt-1">{conv.timestamp.toLocaleTimeString()}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className="lg:col-span-3 card flex flex-col">
          {currentConv && (
            <>
              {/* Header */}
              <div className="p-4 border-b border-blue-500/30 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-white">{currentConv.name}</h2>
                  <p className="text-xs text-blue-300/70">{currentConv.channel}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleStartChat}
                    className="p-2 hover:bg-slate-700/20 rounded-lg transition-colors cursor-pointer">
                    <MessageSquare size={20} className="text-blue-200" />
                  </button>
                  <button
                    onClick={handlePhoneCall}
                    className="p-2 hover:bg-slate-700/20 rounded-lg transition-colors cursor-pointer">
                    <Phone size={20} className="text-blue-200" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {currentConv.messages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'customer' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-xs px-4 py-2 rounded-lg text-sm ${
                      msg.role === 'customer'
                        ? 'bg-slate-700/20 text-white'
                        : 'bg-blue-500/30 text-white'
                    }`}>
                      <p>{msg.text}</p>
                      <p className="text-xs mt-1 opacity-70">{msg.timestamp.toLocaleTimeString()}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="border-t border-blue-500/30 p-4">
                <form onSubmit={handleSendMessage} className="flex space-x-2">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="input-base flex-1"
                  />
                  <button type="submit" className="btn-primary px-4">
                    <Send size={20} />
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
